import React from 'react';
import { Button, Box } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SkipNextOutlinedIcon from '@mui/icons-material/SkipNextOutlined';

interface ProcessingActionButtonsProps {
  hasInProgressStep: () => boolean;
  isBeginProcessingEnabled: () => boolean;
  isMarkAsDoneEnabled: () => boolean;
  shouldShowButton: () => boolean;
  handleBeginProcessing: () => void;
  handleMarkAsDone: () => void;
  shouldShowSkipButton?: () => boolean;
  handleSkipStep?: () => void;
  isActionInProgress?: boolean;
  classes: any;
}

const ProcessingActionButtons: React.FC<ProcessingActionButtonsProps> = ({
  hasInProgressStep,
  isBeginProcessingEnabled,
  isMarkAsDoneEnabled,
  shouldShowButton,
  handleBeginProcessing,
  handleMarkAsDone,
  shouldShowSkipButton,
  handleSkipStep,
  isActionInProgress = false,
  classes,
}) => {
  const showSkip = Boolean(shouldShowSkipButton?.() && handleSkipStep);
  return (
    <>
      {shouldShowButton() && (
        <>
          {/* Mobile Buttons */}
          <Box
            sx={{
              display: { xs: 'flex', md: 'none' },
              flexDirection: 'column',
              gap: 1.25,
              position: 'fixed',
              bottom: 20,
              left: 16,
              right: 16,
              zIndex: 1000,
            }}
          >
            {showSkip && (
              <Button
                variant="outlined"
                onClick={handleSkipStep}
                disabled={isActionInProgress}
                className={classes.labelNoto16Bold400_hh}
                disableRipple
                startIcon={<SkipNextOutlinedIcon />}
                sx={{
                  width: '100%',
                  backgroundColor: '#ffffff',
                  color: '#D8010B',
                  borderColor: '#D8010B',
                  py: 1.75,
                  minHeight: 52,
                  textTransform: 'none',
                  lineHeight: 1.2,
                  borderRadius: '16px',
                  '&:hover': {
                    backgroundColor: '#fff5f5',
                    borderColor: '#D8010B',
                    outline: 'none',
                  },
                  '&:focus': {
                    backgroundColor: '#fff5f5',
                    borderColor: '#D8010B',
                    outline: 'none',
                  },
                  '&:disabled': {
                    backgroundColor: '#ffffff',
                    color: '#bdbdbd',
                    borderColor: '#bdbdbd',
                  },
                }}
              >
                Skip Step
              </Button>
            )}
            {!hasInProgressStep() ? (
              <Button
                variant="contained"
                onClick={handleBeginProcessing}
                disabled={isActionInProgress || !isBeginProcessingEnabled()}
                className={classes.labelNoto16Bold400_hh}
                disableRipple
                sx={{
                  width: '100%',
                  background: '#094BC7',
                  color: 'white',
                  py: 1.75,
                  minHeight: 52,
                  textTransform: 'none',
                  textAlign: 'center',
                  lineHeight: 1.2,
                  borderRadius: '16px',
                  boxShadow: '0 4px 16px rgba(9, 75, 199, 0.4)',
                  '&:hover': {
                    background: '#073a9c',
                    boxShadow: '0 6px 20px rgba(9, 75, 199, 0.5)',
                    outline: 'none',
                  },
                  '&:focus': {
                    background: '#073a9c',
                    boxShadow: '0 6px 20px rgba(9, 75, 199, 0.5)',
                    outline: 'none',
                  },
                  '&:disabled': {
                    background: '#bdbdbd',
                    color: '#fff',
                  },
                }}
              >
                Begin Processing
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleMarkAsDone}
                disabled={isActionInProgress || !isMarkAsDoneEnabled()}
                className={classes.labelNoto16Bold400_hh}
                disableRipple
                sx={{
                  width: '100%',
                  backgroundColor: '#2e7d32',
                  color: 'white',
                  py: 1.75,
                  minHeight: 52,
                  textTransform: 'none',
                  textAlign: 'center',
                  lineHeight: 1.2,
                  borderRadius: '16px',
                  '&:hover': {
                    backgroundColor: '#2e7d32',
                    outline: 'none',
                  },
                  '&:focus': {
                    backgroundColor: '#2e7d32',
                    outline: 'none',
                  },
                  '&:disabled': {
                    backgroundColor: '#bdbdbd',
                    color: '#fff',
                    outline: 'none',
                  },
                }}
              >
                Mark as Done
              </Button>
            )}
          </Box>
          {/* Desktop Button */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'center', mt: 3, mb: 2 }}>
            {!hasInProgressStep() ? (
              <Button
                variant="contained"
                disableRipple
                startIcon={<PlayArrowIcon sx={{ fontSize: '1.95rem' }} />}
                onClick={handleBeginProcessing}
                disabled={isActionInProgress || !isBeginProcessingEnabled()}
                className={classes.labelNoto16Bold400_hh}
                sx={{
                  background: '#094BC7',
                  color: 'white',
                  px: 6,
                  py: 1.5,
                  textTransform: 'none',
                  borderRadius: 2,
                  boxShadow: '0 4px 16px rgba(9, 75, 199, 0.4)',
                  '&:hover': {
                    background: '#073a9c',
                  },
                  '&:disabled': {
                    background: '#bdbdbd',
                    color: '#fff',
                  },
                }}
              >
                Begin Processing
              </Button>
            ) : (
              <Button
                variant="contained"
                disableRipple
                onClick={handleMarkAsDone}
                disabled={isActionInProgress || !isMarkAsDoneEnabled()}
                className={classes.labelNoto16Bold400_hh}
                sx={{
                  backgroundColor: '#2e7d32',
                  color: 'white',
                  px: 6,
                  py: 1.5,
                  textTransform: 'none',
                  borderRadius: 2,
                  '&:hover': {
                    backgroundColor: '#1b5e20',
                  },
                  '&:disabled': {
                    backgroundColor: '#bdbdbd',
                    color: '#fff',
                  },
                }}
              >
                Mark as Done
              </Button>
            )}
          </Box>
        </>
      )}
      <Box sx={{ display: { xs: 'block', md: 'none' }, height: showSkip ? '190px' : '120px' }} />
    </>
  );
};

export default ProcessingActionButtons;
